import type { MutableRefObject } from 'react';
import { useCallback, useMemo, useRef } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { Drawer } from '@/components/ats';
import { FormBuilder } from '@/components/formBuilder';
import { usePushToHris } from '@/components/ats/hris/useHrisIntegrations';
import DocumentsSection from '@/components/ats/hris/document-section/DocumentsSection';
import { getDarwinboxDateFormat } from '@/components/ats/hris/darwinbox/darwinboxFormUtils';
import { ContractEndedNotice } from '@/components/ats/hris/workdayHire/ContractEndedNotice';
import type { ContractEnded } from '@/components/ats/hris/workdayHire/types';

// `DocumentsSection` is a `forwardRef` whose prop types are not expressed;
// aliasing keeps this conversion mechanical rather than retyping that module.
const DocumentsSectionAny = DocumentsSection as any;

// HRIS integrations that support document upload
const DOCUMENT_UPLOAD_SUPPORTED_INTEGRATIONS = new Set([
  'personio',
  'workday',
  'afas',
  'bamboohr',
  'hibob',
  'nmbrs',
  'sagehr',
  'hrworks',
  'youserve',
  'sdworx',
]);

interface EmployeeFormDrawerProps {
  isOpen?: boolean;
  onClose?: () => void;
  integrationName?: string;
  integration?: ({ type?: string; name?: string } & Record<string, any>) | null;
  applicationId?: string | number;
  formConfig?: any[];
  onSubmit?: () => void | Promise<void>;
  onSuccess?: (_result?: unknown) => void;
  formRef?: MutableRefObject<any>;
  initialValues?: Record<string, any>;
  /**
   * Set only when a conversion ended the contract and then failed the hire.
   * The form itself cannot say so — the section that would have is the one the
   * backend drops once there is nothing left to ask.
   */
  contractEnded?: ContractEnded | null;
}

const EmployeeFormDrawer = ({
  isOpen,
  onClose,
  integrationName,
  integration,
  applicationId,
  formConfig,
  onSubmit = () => {},
  onSuccess = () => {},
  formRef,
  initialValues = {},
  contractEnded = null,
}: EmployeeFormDrawerProps) => {
  const { submitEmployeeForm, isPending } = usePushToHris({
    onSuccess: onSuccess,
  });

  const documentsRef = useRef<any>(null);

  // Undefined for a Kombo formConfig (no 'date' field carries rawDateFormat) —
  // submitEmployeeForm only reads this for Darwinbox.
  const darwinboxDateFormat = useMemo(
    () => getDarwinboxDateFormat(formConfig || []),
    [formConfig]
  );

  // Check if the integration supports document upload
  const showDocumentsSection = useMemo(() => {
    const integrationType = integration?.type || '';
    // Extract the tool ID from 'kombo/{id}' format
    const toolId = integrationType.startsWith('kombo/')
      ? integrationType.replace('kombo/', '')
      : integrationType;
    return DOCUMENT_UPLOAD_SUPPORTED_INTEGRATIONS.has(toolId);
  }, [integration?.type]);

  const handleSubmitForm = useCallback(async () => {
    try {
      await formRef.current?.submitForm();
      const errArray = Object.keys(formRef.current?.errors);
      if (errArray?.length) {
        const [firstField] = errArray;
        const $element = document.querySelector(`label[for="${firstField}"]`);
        $element?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }

      if (!formRef.current?.isValid) {
        return false;
      }

      const success = await submitEmployeeForm(integration, applicationId, {
        ...(formRef.current?.values || {}),
        hris_documents: showDocumentsSection
          ? documentsRef.current?.getDocumentsData?.() || []
          : [],
        // Stripped back out in submitEmployeeForm before building the payload —
        // same pattern as hris_documents above.
        __darwinboxDateFormat: darwinboxDateFormat,
      });

      if (success) {
        if (onSubmit) {
          await onSubmit();
        }
      }

      return success;
    } catch {
      onClose();
      return false;
    }
  }, [
    formRef,
    submitEmployeeForm,
    integration,
    applicationId,
    onClose,
    onSubmit,
    showDocumentsSection,
    darwinboxDateFormat,
  ]);

  if (!isOpen) return null;

  const firstName = initialValues?.first_name || initialValues?.firstName || '';
  const lastName = initialValues?.last_name || initialValues?.lastName || '';
  const name = firstName && (lastName ? `${firstName} ${lastName}` : firstName);

  return (
    <Drawer
      drawerActions={{
        isOpen,
        onClose,
      }}
      headerText={`Add ${name || 'employee'} to ${integrationName}`}
      secondaryCtaLabel="Cancel"
      secondaryCtaClick={onClose}
      ctaLabel="Add employee"
      isCtaLoading={isPending}
      onDrawerSubmit={handleSubmitForm}
      ctaProps={{
        isDisabled: isPending,
        minW: '170px',
      }}
      loadingText="Adding"
      hasFooterDivider={true}
      headerTextProps={{
        borderBottom: '1px solid',
        borderBottomColor: 'border-color',
      }}
      drawerBodyProps={{
        overflowY: 'auto',
      }}
    >
      <Flex w="100%" h="100%" flex={1} direction="column" overflow="hidden">
        <Box flex={1} overflowY="auto">
          {contractEnded && (
            <Box mb="4">
              <ContractEndedNotice contractEnded={contractEnded} />
            </Box>
          )}
          <FormBuilder
            config={formConfig}
            formRef={formRef}
            initialValues={initialValues}
          />
          {showDocumentsSection && (
            <DocumentsSectionAny
              ref={documentsRef}
              integration={integration}
              applicationId={applicationId}
            />
          )}
        </Box>
      </Flex>
    </Drawer>
  );
};

export default EmployeeFormDrawer;
